import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import LightboxModal from '../components/LightboxModal';

export default function GalleriesPage() {
  const [galleries, setGalleries] = useState([]);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(0);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/_api/public/content/gallerier')
      .then((res) => {
        if (!res.ok) throw new Error('Kunne ikke hente gallerier');
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data)) {
          const mapped = data.map((gal) => ({
            ...gal,
            title: gal.titel || gal.title,
            description: gal.beskrivelse || gal.description,
            images: gal.billeder || gal.images || []
          }));
          setGalleries(mapped);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const activeGallery = galleries[activeGalleryIndex];
  const images = activeGallery?.images || [];

  return (
    <div className="bg-white pb-20">
      
      {/* Header Banner following container line */}
      <section className="bg-[#eef4e6] border-b border-[#d9e5d4] py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-4">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1D2923] tracking-tight font-serif-heading">
              Gallerier fra Skolelivet
            </h1>
            <p className="text-base sm:text-lg text-[#5A6961] leading-relaxed">
              Oplev glæden, nærværet og de mange forskelligartede aktiviteter på Odder Lille Friskole i billeder.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content following container line */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 lg:pt-14">
        
        {loading ? (
          <div className="text-center py-16">
            <i className="fa-solid fa-spinner fa-spin text-2xl text-[#264A35]" aria-hidden="true" />
            <p className="text-sm text-[#5A6961] mt-2">Indlæser billeder...</p>
          </div>
        ) : galleries.length === 0 ? (
          <div className="text-center py-16 text-[#5A6961]">
            Ingen gallerier fundet.
          </div>
        ) : (
          <>
            {/* Gallery Tabs */}
            <div className="flex flex-wrap items-center gap-2 mb-10">
              {galleries.map((gal, idx) => (
                <button
                  key={gal.id || gal.url_slug}
                  type="button"
                  onClick={() => {
                    setActiveGalleryIndex(idx);
                    setLightboxImageIndex(null);
                  }}
                  className={`px-5 py-2.5 rounded-full text-xs sm:text-sm font-bold transition-all cursor-pointer ${
                    activeGalleryIndex === idx
                      ? 'bg-[#264A35] text-white shadow-xs'
                      : 'bg-white text-[#2D3A32] border border-[#d9e5d4] hover:bg-[#eef4e6]'
                  }`}
                >
                  {gal.title}
                </button>
              ))}
            </div>

            {/* Gallery Description */}
            {activeGallery && (
              <div className="mb-8 max-w-2xl">
                <h2 className="text-2xl font-bold text-[#1D2923] font-serif-heading">
                  {activeGallery.title}
                </h2>
                {activeGallery.description && (
                  <p className="text-base text-[#5A6961] mt-1 leading-relaxed">
                    {activeGallery.description}
                  </p>
                )}
              </div>
            )}

            {/* Image Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6">
              {images.map((imgUrl, index) => (
                <button
                  key={imgUrl}
                  type="button"
                  onClick={() => setLightboxImageIndex(index)}
                  className="group relative rounded-2xl overflow-hidden aspect-4/3 bg-[#eef4e6] shadow-xs hover:shadow-lg transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#264A35]"
                  aria-label={`Åbn billede ${index + 1}`}
                >
                  <img
                    src={imgUrl}
                    alt={`Billede ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/25 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-white/90 text-[#1D2923] flex items-center justify-center shadow-md">
                      <i className="fa-solid fa-expand text-sm" aria-hidden="true" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

      </section>

      {/* Lightbox Modal */}
      <LightboxModal
        images={images}
        activeIndex={lightboxImageIndex}
        onClose={() => setLightboxImageIndex(null)}
        onNext={() => setLightboxImageIndex((prev) => (prev + 1) % images.length)}
        onPrev={() => setLightboxImageIndex((prev) => (prev - 1 + images.length) % images.length)}
      />

    </div>
  );
}
